import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound, LogOut } from 'lucide-react';
import { Avatar, Badge, Input, Separator, cn, initialsFromName } from '@mlm/ui';
import { logout } from '../../lib/api';
import { useAuthStore } from '../../lib/auth-store';
import { NotificationsBell } from './notifications-bell';

/**
 * Phase 13 top header — sticky bar over the content column. Search input
 * routes to /members?q= on Enter (v1 lookup; a real ⌘K palette lands in a
 * follow-up). The notifications bell is real as of LMS Phase 2 — it reads the
 * signed-in admin's own rows, which is where owner alerts land. Avatar
 * dropdown shows the role badge + a Sign-out action.
 */
export function Header() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const searchRef = useRef<HTMLInputElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  function onSearchSubmit(e: React.FormEvent) {
    e.preventDefault();
    const q = searchRef.current?.value.trim();
    if (!q) return;
    navigate(`/members?q=${encodeURIComponent(q)}`);
  }

  async function onLogout() {
    await logout();
    navigate('/login');
  }

  return (
    <header className="h-14 border-b bg-background sticky top-0 z-10 flex items-center gap-3 px-6">
      <form onSubmit={onSearchSubmit} className="flex-1 max-w-md">
        <Input
          ref={searchRef}
          type="search"
          placeholder="Search members, orders, products… (⌘K)"
          className="h-9"
        />
      </form>

      <div className="ml-auto flex items-center gap-3">
        <NotificationsBell />

        <Separator orientation="vertical" className="h-6 mx-1" />

        <div className="relative">
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="flex items-center"
            aria-label="User menu"
          >
            <Avatar size="md" initials={initialsFromName(user?.fullName ?? user?.email ?? '?')} />
          </button>

          {menuOpen ? (
            <div
              className="absolute right-0 mt-2 w-64 rounded-md border bg-popover shadow-md z-20"
              onMouseLeave={() => setMenuOpen(false)}
            >
              <div className="p-3 border-b">
                <p className="text-sm font-medium">{user?.fullName ?? user?.email}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
                {user?.roles?.length ? (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {user.roles.map((r) => (
                      <Badge key={r} variant="secondary">
                        {r}
                      </Badge>
                    ))}
                  </div>
                ) : null}
              </div>
              {/* Changing your own password needs no permission, so it lives
                  here rather than in the perm-filtered sidebar — a leaf under
                  Settings would drag that whole section into view for staff
                  who shouldn't see it. */}
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  navigate('/change-password');
                }}
                className={cn(
                  'flex w-full items-center gap-2 px-3 py-2 text-sm text-left',
                  'hover:bg-muted text-foreground transition-colors',
                )}
              >
                <KeyRound className="h-4 w-4" />
                Change password
              </button>
              <button
                type="button"
                onClick={onLogout}
                className={cn(
                  'flex w-full items-center gap-2 px-3 py-2 text-sm text-left',
                  'hover:bg-muted text-foreground transition-colors',
                )}
              >
                <LogOut className="h-4 w-4" />
                Sign out
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
}
